import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Truck } from "lucide-react";
import { api, SERVICES, SERVICE_AREAS } from "@/lib/api";

const Footer = () => {
  const [config, setConfig] = useState({ business_phone: "(813) 555-0100", business_email: "info@fastjunkpros.com" });

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  return (
    <footer className="mt-24 bg-[hsl(60,6%,11%)] text-[hsl(42,22%,97%)]" data-testid="site-footer">
      <div className="container-wide py-20">
        <div className="rounded-sm border border-white/10 bg-white/5 p-8 sm:p-12">
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div>
              <p className="kicker text-primary">Ready when you are</p>
              <h2 className="mt-2 text-3xl font-black sm:text-4xl lg:text-5xl">Need it hauled? Request a pickup.</h2>
              <p className="mt-3 max-w-xl text-white/70">
                Free quotes, upfront pricing, and same-day service on most jobs.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/contact" className="btn-cta" data-testid="footer-quote-cta">
                Get a Free Hauling Quote
              </Link>
              <a href={`tel:${config.business_phone.replace(/[^0-9+]/g, "")}`} className="btn-ghost-dark" data-testid="footer-call-cta">
                <Phone className="h-4 w-4" /> {config.business_phone}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-10 sm:grid-cols-3 lg:grid-cols-4">
          <div className="col-span-2 sm:col-span-1">
            <div className="flex items-center gap-2 font-display">
              <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-primary">
                <Truck className="h-5 w-5" />
              </span>
              <span className="text-lg font-black uppercase">Fast Junk Pros</span>
            </div>
            <p className="mt-4 text-sm text-white/60">
              Locally owned trash hauling and junk removal. Fast, affordable, and always upfront.
            </p>
            <div className="mt-5 space-y-2 text-sm text-white/70">
              <a href={`tel:${config.business_phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 hover:text-white">
                <Phone className="h-4 w-4 text-primary" /> {config.business_phone}
              </a>
              <a href={`mailto:${config.business_email}`} className="flex items-center gap-2 hover:text-white">
                <Mail className="h-4 w-4 text-primary" /> {config.business_email}
              </a>
            </div>
          </div>

          <div>
            <p className="kicker text-white/60">Services</p>
            <ul className="mt-4 space-y-2 text-sm">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services#${s.slug}`} className="text-white/70 hover:text-white">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="kicker text-white/60">Areas We Serve</p>
            <ul className="mt-4 space-y-2 text-sm">
              {SERVICE_AREAS.map((a) => (
                <li key={a.slug}>
                  <Link to={`/service-areas/${a.slug}`} className="text-white/70 hover:text-white">
                    {a.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="kicker text-white/60">Company</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/how-it-works" className="text-white/70 hover:text-white">How It Works</Link></li>
              <li><Link to="/pricing" className="text-white/70 hover:text-white">Pricing</Link></li>
              <li><Link to="/locations" className="text-white/70 hover:text-white">Locations</Link></li>
              <li><Link to="/blog" className="text-white/70 hover:text-white">Blog</Link></li>
              <li><Link to="/about" className="text-white/70 hover:text-white">About</Link></li>
              <li><Link to="/faq" className="text-white/70 hover:text-white">FAQ</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-white">Contact</Link></li>
              <li><Link to="/privacy-policy" className="text-white/40 hover:text-white">Privacy</Link></li>
              <li><Link to="/terms-of-service" className="text-white/40 hover:text-white">Terms</Link></li>
              <li><Link to="/disclaimer" className="text-white/40 hover:text-white">Disclaimer</Link></li>
              <li><Link to="/admin/login" className="text-white/40 hover:text-white">Admin</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/50 sm:flex-row">
          <p>© {new Date().getFullYear()} Fast Junk Pros — Local hauling made easy.</p>
          <p>Free quotes · Upfront pricing · Same-day service on most jobs.</p>
        </div>
        <p className="mt-4 max-w-4xl text-[11px] leading-relaxed text-white/40">
          Fast Junk Pros works to connect customers with reliable local hauling partners. Availability may vary by
          location. Starting prices shown are estimates only — final pricing depends on load size, material type,
          stairs, distance, urgency, and disposal requirements.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
